'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { createBrowserClient } from '@/lib/supabase/client';

// Delay (ms) before the card appears on the cover-progress screen.
export const COVER_NOTIFY_CARD_DELAY_MS = 15_000;

declare global {
  interface Window {
    turnstile: {
      render: (element: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

interface Props {
  bookId: string;
  sessionId: string;
  onDismiss?: () => void;
}

type AuthState =
  | { status: 'loading' }
  | { status: 'authenticated'; email: string }
  | { status: 'guest' };

type CardView =
  | 'main'        // authenticated: show one-tap; guest: show Google + "or use email" toggle
  | 'email-form'  // guest: email + Turnstile input
  | 'code-input'  // guest: 6-digit code entry
  | 'done';       // success for both paths

export default function CoverNotifyCard({ bookId, sessionId, onDismiss }: Props) {
  const [authState, setAuthState] = useState<AuthState>({ status: 'loading' });
  const [view, setView] = useState<CardView>('main');

  // Guest email-form state
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileWidgetId, setTurnstileWidgetId] = useState<string | null>(null);
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const [turnstileError, setTurnstileError] = useState(false);

  const turnstileContainerRef = useRef<HTMLDivElement>(null);

  // --- Auth detection on mount ---
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const supabase = createBrowserClient();
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session?.user?.email) {
          setAuthState({ status: 'authenticated', email: session.user.email });
        } else {
          setAuthState({ status: 'guest' });
        }
      } catch {
        if (!cancelled) setAuthState({ status: 'guest' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // --- Load Turnstile script when guest email-form becomes active ---
  useEffect(() => {
    if (view !== 'email-form' || authState.status !== 'guest') return;
    if (!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY) {
      setTurnstileError(true);
      return;
    }

    const existing = document.querySelector('script[src*="challenges.cloudflare.com/turnstile"]');
    if (existing) {
      setTurnstileLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.onload = () => setTurnstileLoaded(true);
    script.onerror = () => setTurnstileError(true);
    document.body.appendChild(script);

    return () => {
      if (turnstileWidgetId && window.turnstile) {
        try { window.turnstile.remove(turnstileWidgetId); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  // --- Initialise Turnstile widget once the script has loaded ---
  useEffect(() => {
    if (!turnstileLoaded || turnstileError || !turnstileContainerRef.current) return;

    const container = turnstileContainerRef.current;
    if (container.hasChildNodes()) return; // Already rendered.

    const timer = setTimeout(() => {
      if (!window.turnstile) return;
      try {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        const id = window.turnstile.render(container, {
          sitekey: process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY!,
          callback: (token: string) => { setTurnstileToken(token); setError(''); },
          'error-callback': () => { setTurnstileError(true); setError('Verification failed. Please try again.'); },
          'expired-callback': () => setTurnstileToken(''),
          size: isMobile ? 'compact' : 'normal',
          theme: 'light',
          retry: 'never',
          'refresh-expired': 'never',
          appearance: 'always',
        });
        setTurnstileWidgetId(id);
      } catch (e) {
        console.error('[CoverNotifyCard] Turnstile init error', e);
        setTurnstileError(true);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [turnstileLoaded, turnstileError]);

  // --- POST helpers ---
  const headers = useCallback(
    () => ({ 'Content-Type': 'application/json', 'x-session-id': sessionId }),
    [sessionId],
  );

  const handleAuthTap = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Send the access token so the server can recognize us even when the session lives only in
      // localStorage (no auth cookie) — otherwise the route falls through to the guest path.
      const { data: { session } } = await createBrowserClient().auth.getSession();
      const token = session?.access_token;
      const res = await fetch('/api/books/request-cover-notify', {
        method: 'POST',
        headers: { ...headers(), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ bookId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setView('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [bookId, headers]);

  const handleGoogleSignIn = useCallback(() => {
    const supabase = createBrowserClient();
    const redirectTo =
      `${window.location.origin}/auth/callback?next=` +
      encodeURIComponent(`/book/${bookId}/preview?coverBookId=${bookId}&coverSid=${sessionId}`);
    supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } });
  }, [bookId, sessionId]);

  const handleEmailRequest = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email) { setError('Please enter your email'); return; }
    if (!turnstileToken && !turnstileError) { setError('Please complete the verification'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/books/request-cover-notify', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ bookId, email, turnstileToken: turnstileToken || 'error-loading-turnstile' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setView('code-input');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
      if (turnstileWidgetId && window.turnstile) {
        try { window.turnstile.reset(turnstileWidgetId); } catch {}
      }
      setTurnstileToken('');
    } finally {
      setLoading(false);
    }
  }, [bookId, email, headers, turnstileToken, turnstileError, turnstileWidgetId]);

  const handleCodeVerify = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (code.length !== 6) { setError('Please enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await fetch('/api/books/verify-cover-notify', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ bookId, email, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Invalid code');
      setView('done');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, [bookId, email, code, headers]);

  if (authState.status === 'loading') return null;

  // --- Authenticated ---
  if (authState.status === 'authenticated') {
    return (
      <div className="relative bg-white border border-purple-200 rounded-xl shadow-md p-5 max-w-sm w-full">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        {view === 'done' ? (
          <p className="text-green-700 font-medium text-sm">Done — we&apos;ll email you when your cover is ready.</p>
        ) : (
          <>
            <p className="text-gray-700 text-sm mb-3">
              We&apos;ll email your cover to <span className="font-semibold">{authState.email}</span> when it&apos;s ready.
            </p>
            {error && <p className="text-red-600 text-xs mb-2">{error}</p>}
            <button
              onClick={handleAuthTap}
              disabled={loading}
              className="w-full py-2 px-4 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Saving…' : 'Notify me'}
            </button>
          </>
        )}
      </div>
    );
  }

  // --- Guest: success ---
  if (view === 'done') {
    return (
      <div className="relative bg-white border border-purple-200 rounded-xl shadow-md p-5 max-w-sm w-full">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        <p className="text-green-700 font-medium text-sm">Done — we&apos;ll email you when your cover is ready.</p>
      </div>
    );
  }

  // --- Guest: code entry ---
  if (view === 'code-input') {
    return (
      <div className="relative bg-white border border-purple-200 rounded-xl shadow-md p-5 max-w-sm w-full">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        <p className="text-gray-700 text-sm mb-3">We sent a 6-digit code to <span className="font-semibold">{email}</span>. Enter it below.</p>
        <form onSubmit={handleCodeVerify} className="space-y-3">
          <input
            type="text"
            inputMode="numeric"
            maxLength={6}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="123456"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-center text-lg tracking-widest focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            disabled={loading}
          />
          {error && <p className="text-red-600 text-xs">{error}</p>}
          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full py-2 px-4 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Verifying…' : 'Confirm'}
          </button>
        </form>
      </div>
    );
  }

  // --- Guest: email form ---
  if (view === 'email-form') {
    return (
      <div className="relative bg-white border border-purple-200 rounded-xl shadow-md p-5 max-w-sm w-full">
        <button
          type="button"
          onClick={onDismiss}
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Dismiss"
        >
          <X className="w-5 h-5" />
        </button>
        <p className="text-gray-700 text-sm font-medium mb-1">Get notified by email</p>
        <p className="text-xs text-gray-500 mb-3">
          Tip: sign in with Google to track your book on any device.
        </p>
        <form onSubmit={handleEmailRequest} className="space-y-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            disabled={loading}
          />
          {!turnstileError && (
            <div className="flex justify-center">
              <div ref={turnstileContainerRef} />
            </div>
          )}
          {turnstileError && (
            <p className="text-amber-600 text-xs text-center">Verification temporarily unavailable</p>
          )}
          {error && <p className="text-red-600 text-xs">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 px-4 rounded-lg text-sm font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Sending…' : 'Send code'}
          </button>
          <button
            type="button"
            onClick={() => setView('main')}
            className="w-full text-xs text-gray-500 hover:text-gray-700 underline"
          >
            Back
          </button>
        </form>
      </div>
    );
  }

  // --- Guest: main view (Google + email reveal) ---
  return (
    <div className="relative bg-white border border-purple-200 rounded-xl shadow-md p-5 max-w-sm w-full">
      <button
        type="button"
        onClick={onDismiss}
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Dismiss"
      >
        <X className="w-5 h-5" />
      </button>
      <p className="text-gray-800 text-sm font-medium mb-1">Cover taking a moment?</p>
      <p className="text-xs text-gray-500 mb-4">
        Get an email when it&apos;s ready — even if you close this tab. Sign in with Google so you can
        open your book on any device, including your phone or a shared computer.
      </p>
      <button
        onClick={handleGoogleSignIn}
        className="w-full flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium border border-gray-300 bg-white hover:bg-gray-50 transition-colors mb-2"
      >
        {/* Inline Google logo SVG — no external dependency */}
        <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
          <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
          <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
          <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
          <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
        </svg>
        Continue with Google
      </button>
      <button
        onClick={() => setView('email-form')}
        className="w-full text-xs text-gray-500 hover:text-gray-700 underline"
      >
        or use your email
      </button>
    </div>
  );
}
